import { isManagerRole } from "../helpers/roles";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { useFopLabels } from "../hooks/useFopLabels";
import { TodayUnfiscalizedOrders } from "../components/TodayUnfiscalizedOrders/TodayUnfiscalizedOrders.jsx";

// ─── Кеш платежів ────────────────────────────────────────────────────────────
// Ключ: "url:start:end". Минулі дати зберігаються вічно, сьогодні — 5 хв.
const _payCache = new Map();
const _CACHE_TTL = 5 * 60 * 1000; // 5 хв

function _cacheKey(slug, start, end) { return `${slug}:${start}:${end}`; }

function _isPastPeriod(endTs) {
    // end < початок сьогоднішнього дня → минулий день/місяць
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    return endTs * 1000 < todayStart.getTime();
}

function getPayCache(slug, start, end) {
    const e = _payCache.get(_cacheKey(slug, start, end));
    if (!e) return null;
    if (!_isPastPeriod(end) && Date.now() - e.at > _CACHE_TTL) {
        _payCache.delete(_cacheKey(slug, start, end));
        return null;
    }
    return e.data;
}

function setPayCache(slug, start, end, data) {
    _payCache.set(_cacheKey(slug, start, end), { data, at: Date.now() });
}

async function fetchFopPayments(slug, fetchUrl, start, end) {
    const cached = getPayCache(fetchUrl, start, end);
    if (cached) return cached;
    try {
        const res = await fetch(`${fetchUrl}?startDate=${start}&endDate=${end}`);
        const raw = res.ok ? await res.json() : [];
        const data = (Array.isArray(raw) ? raw : []).filter((x) => x.amount > 0);
        setPayCache(fetchUrl, start, end, data);
        return data;
    } catch {
        return [];
    }
}
// ─────────────────────────────────────────────────────────────────────────────

const PaymentsPage = ({user}) => {
    const { labelBySlug: FOP_NAMES, slugs: FOP_IDS } = useFopLabels();
    const [startDate, setStartDate] = useState(new Date());
    const [payments, setPayments] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [selectedFop, setSelectedFop] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [monthlyStats, setMonthlyStats] = useState([]);
    const [showStatsButton, setShowStatsButton] = useState(false);
    const [loadingStats, setLoadingStats] = useState(false);

    let timeLeft = 20;

    const [countdown, setCountdown] = useState(timeLeft); // N секунд до показа кнопки



    const role = user?.role;




    const getTimestampRange = (date) => {
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(date);
        end.setHours(23, 59, 59, 999);
        return {
            start: Math.floor(start.getTime() / 1000),
            end: Math.floor(end.getTime() / 1000),
        };
    };

    const getMonthTimestampRange = (date) => {
        const start = new Date(date.getFullYear(), date.getMonth(), 1);
        const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
        return {
            start: Math.floor(start.getTime() / 1000),
            end: Math.floor(end.getTime() / 1000),
        };
    };

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const fetchWithDelay = async (ids, start, end) => {
        if (!ids.length) return [];
        const results = [];
        for (const id of ids) {
            const isCached = !!getPayCache(`/api/payments/${id}`, start, end);
            const data = await fetchFopPayments(id, `/api/payments/${id}`, start, end);
            results.push(...data.map((item) => ({ ...item, fop: id })));
            // затримка тільки якщо реально ходили в мережу
            if (!isCached) await delay(1000);
        }
        return results;
    };



    const fetchAllPayments = async () => {
        if (!FOP_IDS.length) {
            setPayments([]);
            setLoading(false);
            return;
        }
        setShowStatsButton(false);
        setLoading(true);
        setError("");
        const { start, end } = getTimestampRange(startDate);
        try {
            // Завжди завантажуємо ВСІ ФОПи — селект фільтрує клієнтськи
            const responses = await Promise.all(
                FOP_IDS.map((id) =>
                    fetchFopPayments(id, `/api/payments/${id}`, start, end)
                        .then((data) => data.map((item) => ({ ...item, fop: id })))
                )
            );
            const all = responses.flat().sort((a, b) => b.time - a.time);
            setPayments(all);
        } catch (err) {
            console.error("Помилка завантаження платежів:", err);
            setError("Помилка під час завантаження платежів. Спробуйте пізніше.");
        } finally {
            setLoading(false);
        }
    };

    const handleStatsClick = async () => {
        if (!FOP_IDS.length) return;
        setError("");
        setLoadingStats(true);
        const { start, end } = getMonthTimestampRange(startDate);
        try {
            const allPayments = await fetchWithDelay(FOP_IDS, start, end);
            // setPayments(allPayments);
            // setFiltered(allPayments);

            // Расчёт по ФОПам
            const byFop = {};
            for (let p of allPayments) {
                byFop[p.fop] = (byFop[p.fop] || 0) + p.amount;
            }
            const total = Object.values(byFop).reduce((sum, val) => sum + val, 0);
            const stats = Object.entries(byFop)
                .map(([fop, amount]) => ({
                    fop,
                    amount,
                    percent: total ? (amount / total * 100).toFixed(1) : 0,
                }))
                .sort((a, b) => b.amount - a.amount); // сортировка по убыванию

            setMonthlyStats(stats);
        } catch (err) {
            console.error("Помилка:", err);
            setError("Не вдалося завантажити платежі.");
        } finally {
            setLoadingStats(false);
        }
    };




    // Перезавантажуємо дані тільки при зміні дати або списку ФОПів
    useEffect(() => {
        fetchAllPayments();
    }, [startDate, FOP_IDS.join(",")]);

    // Селект — клієнтський фільтр по вже завантажених payments
    useEffect(() => {
        setFiltered(selectedFop ? payments.filter((p) => p.fop === selectedFop) : payments);
    }, [payments, selectedFop]);

    useEffect(() => {
        if (!loading && filtered.length > 0) {
            setCountdown(timeLeft);
            const interval = setInterval(() => {
                timeLeft -= 1;
                setCountdown(timeLeft);
                if (timeLeft === 0) {
                    clearInterval(interval);
                    setShowStatsButton(true);
                }
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [loading, filtered]);

    const getTime = (ts) => new Date(ts * 1000).toLocaleTimeString();

    return (
        <div className="payments-page flex-fill">
            {/* Toolbar */}
            <div className="payments-toolbar">
                <div className="d-flex align-items-center gap-2 mb-2">
                    <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        className="form-control payments-datepicker"
                    />
                    <select
                        className="form-select payments-fop-select"
                        value={selectedFop || ''}
                        onChange={(e) => setSelectedFop(e.target.value || null)}
                    >
                        <option value="">Усі акаунти</option>
                        {FOP_IDS.map((id) => (
                            <option key={id} value={id}>{FOP_NAMES[id]}</option>
                        ))}
                    </select>
                    {showStatsButton ? (
                        <Button
                            variant="dark"
                            className="text-white border-0 rounded-3 px-4 py-2 flex-shrink-0"
                            onClick={handleStatsClick}
                            disabled={loadingStats}
                        >
                            📊 Розподіл {loadingStats && (
                                <Spinner animation="border" size="sm" role="status">
                                    <span className="visually-hidden">Завантаження...</span>
                                </Spinner>
                            )}
                        </Button>
                    ) : (
                        <span className="text-muted small flex-shrink-0">Розподіл через {countdown} сек.</span>
                    )}
                </div>
            </div>

            <div className="payments-unfiscalized">
                <TodayUnfiscalizedOrders />
            </div>

            {monthlyStats.length > 0 && (
                <div className="payments-list pt-0">
                    <ul className="list-group">
                        {monthlyStats.map(({ fop, amount, percent }) => (
                            <li key={fop} className="list-group-item d-flex justify-content-between align-items-center border-0 p-0 mb-2">
                                {FOP_NAMES[fop]}
                                <div className="d-flex gap-3 align-items-center">
                                    {!isManagerRole(role) && (
                                        <span>{(amount / 100).toFixed(2)} грн</span>
                                    )}
                                    <span className="badge bg-primary rounded-pill">{percent}%</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="payments-list">
                {loading ? (
                    <div className="text-center">
                        <Spinner animation="border" />
                    </div>
                ) : error ? (
                    <p className="text-danger text-center">{error}</p>
                ) : filtered.length === 0 ? (
                    <p className="text-muted">Немає транзакцій за вибрану дату</p>
                ) : (
                    filtered.map((payment, idx) => (
                        <div className="payment-row-card" key={idx}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>{payment.amount / 100} грн</strong><br/>
                                    <small className="text-muted">{payment.description.replace(/^Від:\s*/i, '')}</small>
                                </div>
                                <div className="text-end">
                                    <div className="text-muted">{getTime(payment.time)}</div>
                                    <div className="small text-muted">{FOP_NAMES[payment.fop]}</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default PaymentsPage;
